import React, { useState, useEffect } from 'react';
import {
  Button, TextField, Grid, Box, Typography, Modal,
  Autocomplete, Link
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { investorApi, vendorApi } from '../../api/api';

const Message = () => {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [notificationList, setNotificationList] = useState([]);
  const [users, setUsers] = useState([]);

  const handleTitleClick = (row) => {
    setSelectedRow(row);
    setOpened(true);
  };

  const handleModalClose = () => {
    setOpened(false);
    setSelectedRow(null);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const getAllNotifications = async () => {
    try {
      const response = await vendorApi.getAllMessages();
      const data = response.data;

      const formattedData = data.map((item) => ({
        id: item.id,
        userid: item.UserId,
        username: item.UserName,
        title: item.Message,
        createdTime: item.Created_On,
        isread: item.STATUS
      }));

      setNotificationList(formattedData);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const getAllInvestors = async () => {
    try {
      const response = await investorApi.getAllUserDetails();
      const data = response.data;

      const formattedInvestors = data.map((investor) => ({
        id: investor.ID,
        name: investor.Username,
      }));

      setUsers(formattedInvestors);
    } catch (error) {
      console.error("Error fetching investors:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([getAllNotifications(), getAllInvestors()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleSendNotification = async () => {
    if (!selectedUser || !newTitle.trim() || !newDescription.trim()) {
      alert("All fields are required.");
      return;
    }

    const newNotification = {
      UserId: selectedUser.id,
      Title: newTitle.trim(),
      Comments: newDescription.trim(),
      Status: "Unread",
    };

    try {
      const response = await vendorApi.addNotification(newNotification);

      if (response?.data?.status === "success") {
        alert("Notification sent successfully.");
        setOpen(false);
        setNewTitle('');
        setNewDescription('');
        setSelectedUser(null);
        getAllNotifications(); // Refresh notifications
      } else {
        alert(`Error: ${response?.data?.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Unread': return 'red';
      case 'Read': return 'blue';
      default: return '#000000';
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'userid', headerName: 'User ID', width: 120 },
    { field: 'username', headerName: 'User Name', width: 150 },
    {
      field: 'title',
      headerName: 'Message',
      width: 220,
      renderCell: (params) => (
        <Link
          href="#"
          onClick={() => handleTitleClick(params.row)}
          underline="hover"
          style={{ cursor: 'pointer', color: 'blue' }}
        >
          {params.value}
        </Link>
      ),
    },
    { field: 'createdTime', headerName: 'Created Time', width: 180 },
    {
      field: 'isread',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <span style={{ color: getStatusColor(params.value), fontWeight: 'bold' }}>
          {params.value}
        </span>
      ),
    },
  ];

  return (
    <div className="container">
      <Box sx={{ flexGrow: 1, border: '2px solid lightgray', borderRadius: '5px', mt: 4, p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Typography variant="h4">Message</Typography>
          </Grid>
          <Grid item xs={4}>
            <Button
              variant="contained"
              onClick={handleOpen}
              color="success"
              sx={{ float: 'right', backgroundColor: 'blueviolet' }}
            >
              Send
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ height: 450, width: '100%', mt: 3 }}>
        <DataGrid
          rows={notificationList}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          autoHeight={false}
        />
      </Box>

      {/* Notification Modal */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', width: 400,
          bgcolor: 'background.paper', border: '2px solid #000',
          boxShadow: 24, p: 4
        }}>
          <Typography variant="h6" gutterBottom>Send Notification</Typography>

          <Autocomplete
            options={users}
            getOptionLabel={(option) => option.name}
            value={selectedUser}
            onChange={(event, newValue) => setSelectedUser(newValue)}
            renderInput={(params) => <TextField {...params} label="User Name" fullWidth />}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Title"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" color="primary" onClick={handleSendNotification}>
            Send
          </Button>
        </Box>
      </Modal>

      {/* View Notification Modal */}
      <Modal open={opened} onClose={handleModalClose}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', width: 500,
          bgcolor: 'background.paper', border: '2px solid #000',
          boxShadow: 24, p: 4
        }}>
          {selectedRow && (
            <>
              <Typography variant="h6">{selectedRow.title}</Typography>
              <Typography sx={{ mt: 2 }}>
                From: {selectedRow.username} (ID: {selectedRow.userid})
              </Typography>
              <Typography sx={{ mt: 1, mb: 2 }}>
                Sent On: {selectedRow.createdTime}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Status: <span style={{ color: getStatusColor(selectedRow.isread) }}>{selectedRow.isread}</span>
              </Typography>
              {/* You can add reply UI here if needed */}
              <Button onClick={handleModalClose} variant="contained">Close</Button>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default Message;
